import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  useContractSimulations,
  useCreateSimulation,
  useResiduals,
  useShares,
} from '../../api/hooks';
import type { ContractView, ShareView } from '../../api/types';
import {
  Badge,
  Button,
  DataTable,
  FormField,
  Modal,
  TextInput,
  useToast,
} from '../../components/DesignSystem';
import type { DataTableColumn } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';
import { formatCurrency, formatNumber, formatPercent } from '../../lib/format';

interface ContractDetailModalProps {
  contract: ContractView;
  showName: string;
  seasonName: string;
  onClose: () => void;
}

/** Read-only contract drill-down: shares, residuals, and simulations. */
export function ContractDetailModal({
  contract,
  showName,
  seasonName,
  onClose,
}: ContractDetailModalProps) {
  const navigate = useNavigate();
  const shares = useShares(contract.id);
  const residuals = useResiduals(contract.id);
  const simulations = useContractSimulations(contract.id);
  const createSimulation = useCreateSimulation();
  const { showToast } = useToast();
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  async function handleCreateSimulation() {
    try {
      const created = await createSimulation.mutateAsync({
        contractId: contract.id,
        ...(contract.seasonId === null ? {} : { seasonId: contract.seasonId }),
        periodStart,
        periodEnd,
      });
      navigate(`/simulation/${created.id}`);
    } catch (error) {
      showToast(errorMessage(error, 'Could not create simulation'), 'error');
    }
  }

  const shareColumns: DataTableColumn<ShareView>[] = [
    { key: 'partyName', header: 'Party' },
    { key: 'saleType', header: 'Sale type', render: (row) => row.saleType ?? 'All' },
    { key: 'territory', header: 'Territory', render: (row) => row.territory ?? 'All' },
    { key: 'level', header: 'Level', render: (row) => row.level ?? '1' },
    { key: 'basis', header: 'Basis' },
    { key: 'value', header: 'Value', align: 'right', render: (row) => formatNumber(row.value) },
    { key: 'source', header: 'Source' },
  ];
  const sharesRows = shares.data ?? [];

  const residualRows = residuals.data ?? [];

  return (
    <Modal
      actions={
        <Button onClick={onClose} type="button" variant="secondary">
          Close
        </Button>
      }
      title={contract.title}
    >
      <div className="rfp-modal-wide rfp-stack">
        <p className="rfp-muted-text">
          <Badge tone={contract.contractType === 'primary' ? 'primary' : 'teal'}>
            {contract.contractType}
          </Badge>{' '}
          {showName}
          {seasonName !== '—' ? ` · ${seasonName}` : ''}
        </p>

        <div>
          <p className="rfp-step-label">— Backend Shares</p>
          {shares.isLoading ? (
            <p className="rfp-loading">Loading…</p>
          ) : (
            <DataTable
              columns={shareColumns}
              emptyMessage="No shares"
              rowKey={(row) => row.id}
              rows={sharesRows}
            />
          )}
        </div>

        <div>
          <p className="rfp-step-label">— Residuals</p>
          {residuals.isLoading ? (
            <p className="rfp-loading">Loading…</p>
          ) : (
            <DataTable
              columns={[
                { key: 'residualType', header: 'Type' },
                { key: 'partyName', header: 'Party' },
                {
                  key: 'rate',
                  header: 'Rate',
                  align: 'right',
                  render: (row) => formatPercent(row.rate),
                },
                {
                  key: 'amount',
                  header: 'Amount',
                  align: 'right',
                  render: (row) => formatCurrency(row.amount),
                },
              ]}
              emptyMessage="No residuals"
              rowKey={(row) => row.id}
              rows={residualRows}
            />
          )}
        </div>

        <div>
          <p className="rfp-step-label">— Simulations</p>
          <div className="rfp-form-grid" style={{ marginBottom: 16 }}>
            <FormField htmlFor="simulation-period-start" label="Reporting period start">
              <TextInput
                id="simulation-period-start"
                onChange={(event) => setPeriodStart(event.target.value)}
                type="date"
                value={periodStart}
              />
            </FormField>
            <FormField htmlFor="simulation-period-end" label="Reporting period end">
              <TextInput
                id="simulation-period-end"
                onChange={(event) => setPeriodEnd(event.target.value)}
                type="date"
                value={periodEnd}
              />
            </FormField>
            <Button
              disabled={
                createSimulation.isPending ||
                periodStart === '' ||
                periodEnd === '' ||
                periodEnd < periodStart
              }
              onClick={() => void handleCreateSimulation()}
              type="button"
              variant="secondary"
            >
              Create period simulation
            </Button>
          </div>
          {simulations.isLoading ? (
            <p className="rfp-loading">Loading…</p>
          ) : (simulations.data ?? []).length === 0 ? (
            <p className="rfp-muted-text">
              No simulations yet — import sales data to create draft simulations.
            </p>
          ) : (
            (simulations.data ?? []).map((simulation) => (
              <section className="rfp-card" key={simulation.id}>
                <div className="rfp-card__head">
                  <div>
                    <h3 className="rfp-card__title">
                      {simulation.periodStart ?? 'Choose a reporting period'}
                      {simulation.periodEnd ? ` to ${simulation.periodEnd}` : ''}
                    </h3>
                    <p className="rfp-card__caption">
                      Review levels, run calculations and download a party statement.
                    </p>
                  </div>
                  <Badge>{simulation.status}</Badge>
                </div>
                <Link className="simulation-open" to={`/simulation/${simulation.id}`}>
                  Open simulation →
                </Link>
              </section>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
